import { ReactNode, useState } from "react";
import cln from "classnames";

import { Link, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/shared/lib/hooks";
import { logout, selectCurrentRole } from "@/features/auth";
import { RolesType, UserRole } from "@/entities/user";
import { CustomModal, ModalType } from "@/shared/ui";
import { selectIsSidebarCollapsed, toggleSidebar } from "@/features/uiSettings";
import { AppRoutes } from "@/shared/config/routes";
import {
  Courses,
  FullLogo,
  Logo,
  Coupons,
  Payments,
  Users,
  Exit,
  ArrowRight,
  ArrowLeft,
  Quote,
  Rewards,
  Koala,
  Notification,
} from "@/shared/assets";

import cls from "./sidebar.module.scss";

interface SidebarProps {
  className?: string;
}

interface NavItem {
  path: string;
  icon: ReactNode;
  label: string;
  roles: RolesType[];
}

const ADMIN_ONLY: RolesType[] = [UserRole.ADMIN];
const ADMIN_AND_TEACHER: RolesType[] = [UserRole.ADMIN, UserRole.TEACHER];

const navItems: NavItem[] = [
  {
    path: AppRoutes.COURSES,
    icon: <Courses />,
    label: "Курсы",
    roles: ADMIN_AND_TEACHER,
  },
  {
    path: AppRoutes.WORDS,
    icon: <Quote />,
    label: "Слова дня",
    roles: ADMIN_ONLY,
  },
  {
    path: AppRoutes.REWARDS,
    icon: <Rewards />,
    label: "Серия входов",
    roles: ADMIN_ONLY,
  },
  {
    path: AppRoutes.USERS,
    icon: <Users />,
    label: "Пользователи",
    roles: ADMIN_AND_TEACHER,
  },
  {
    path: AppRoutes.PAYMENTS,
    icon: <Payments />,
    label: "Оплаты",
    roles: ADMIN_ONLY,
  },
  {
    path: AppRoutes.COUPONS,
    icon: <Coupons />,
    label: "Купоны",
    roles: ADMIN_ONLY,
  },
  {
    path: AppRoutes.KOALA_PHRASES,
    icon: <Koala />,
    label: "Фразы коалы",
    roles: ADMIN_ONLY,
  },
  {
    path: AppRoutes.NOTIFICATIONS,
    icon: <Notification />,
    label: "Уведомления",
    roles: ADMIN_ONLY,
  },
];

export const Sidebar = ({ className }: SidebarProps) => {
  const isCollapsed = useAppSelector(selectIsSidebarCollapsed);
  const role = useAppSelector(selectCurrentRole);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const handleLogoutClick = () => {
    setIsLogoutModalOpen(true);
  };

  const handleLogoutConfirm = () => {
    dispatch(logout());
    setIsLogoutModalOpen(false);
  };

  const handleLogoutCancel = () => {
    setIsLogoutModalOpen(false);
  };

  const toggleCollapse = () => {
    dispatch(toggleSidebar());
  };

  const visibleNavItems = navItems.filter((item) => item.roles.includes(role));

  return (
    <nav
      className={cln(cls.sidebar, className, {
        [cls.collapsed]: isCollapsed,
      })}
    >
      <div className={cls.logoSection}>
        <Link to={AppRoutes.COURSES} className={cls.logo}>
          {isCollapsed ? <Logo /> : <FullLogo />}
        </Link>
        <button
          className={cls.collapseButton}
          onClick={toggleCollapse}
          aria-label={isCollapsed ? "Развернуть" : "Свернуть"}
        >
          {isCollapsed ? <ArrowRight /> : <ArrowLeft />}
        </button>
      </div>

      <ul className={cls.navList}>
        {visibleNavItems.map((item) => {
          let isActive: boolean;

          // логика для главной страницы курсов (/)
          if (item.path === AppRoutes.COURSES) {
            isActive =
              location.pathname === AppRoutes.COURSES ||
              location.pathname.startsWith(AppRoutes.COURSE);
          } else {
            isActive = location.pathname.includes(item.path);
          }

          return (
            <li key={item.path}>
              <Link
                to={item.path}
                className={cln(cls.navItem, {
                  [cls.active]: isActive,
                })}
                title={isCollapsed ? item.label : undefined}
              >
                <span className={cls.icon}>{item.icon}</span>
                {!isCollapsed && (
                  <span className={cls.label}>{item.label}</span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>

      <div className={cls.footer}>
        <button
          className={cls.logoutButton}
          onClick={handleLogoutClick}
          title={isCollapsed ? "Выйти" : undefined}
        >
          <span className={cls.icon}>
            <Exit />
          </span>
          {!isCollapsed && <span className={cls.label}>Выйти</span>}
        </button>
      </div>

      <CustomModal
        isModalOpen={isLogoutModalOpen}
        onCancel={handleLogoutCancel}
        handleOk={handleLogoutConfirm}
        type={ModalType.LOGOUT}
      />
    </nav>
  );
};
